import {EntityType} from "../../gd/const/item"
import {RelationType} from "./const"
import ModelEntity from "../model-entity"
import {relationEntityFromJson} from "./helpers/entity-factory"

// The end to show is whichever is NOT the item being viewed: source / target is
// provenance (who linked whom), and the current item can be either of them.
function pickRelatedItem(relation: Relation, currentItem: ModelEntity): ModelEntity | null {

    const isCurrent = (itemType: EntityType, itemId: ID) =>
        itemType === currentItem.itemType && itemId === currentItem.id

    if (isCurrent(relation.sourceItemType, relation.sourceItemId)) return relation.targetItem
    if (isCurrent(relation.targetItemType, relation.targetItemId)) return relation.sourceItem

    return null     // the link does not touch the current item
}

// One public.relation row: both endpoints, plus the one of them this viewer
// sees. `id` is the LINK's id -- the same item can appear under two links.
export default class Relation {

    public id               : ID
    public type             : RelationType
    public metadata         : any           // relation.metadata jsonb (e.g. {projectViewId} for todo-board links)

    public sourceItemType   : EntityType
    public sourceItemId     : ID
    public targetItemType   : EntityType
    public targetItemId     : ID

    // Display-level stubs (see helpers/entity-factory); either is null when
    // there is nothing to build (deleted / no access / unrendered type).
    public sourceItem       : ModelEntity | null
    public targetItem       : ModelEntity | null

    private otherEnd        : ModelEntity | null

    constructor(data: any, currentItem: ModelEntity) {
        this.id             = data.id
        this.type           = data.type
        this.metadata       = data.metadata || {}

        this.sourceItemType = data.sourceItemType
        this.sourceItemId   = data.sourceItemId
        this.targetItemType = data.targetItemType
        this.targetItemId   = data.targetItemId

        this.sourceItem     = data.sourceItem || null
        this.targetItem     = data.targetItem || null

        this.otherEnd       = pickRelatedItem(this, currentItem)
    }

    // From a live `relation` modelBus event: the raw row with both endpoints
    // already embedded.
    static fromSessionEvent(payload: any, currentItem: ModelEntity): Relation {
        return new Relation({
            ...payload,
            sourceItem: relationEntityFromJson(payload.sourceItemType, payload.sourceItem),
            targetItem: relationEntityFromJson(payload.targetItemType, payload.targetItem),
        }, currentItem)
    }

    // The item this viewer sees. Valid on a relation the collection kept -- it
    // drops everything canShow rejects.
    get relatedItem(): ModelEntity {
        return this.otherEnd!
    }

    // Nothing to show, or a USER link -- the backend creates those from
    // mentions and they are never listed.
    get canShow(): boolean {
        return !!this.otherEnd && this.otherEnd.itemType !== EntityType.USER
    }

    get itemType(): EntityType {
        return this.relatedItem.itemType
    }

    // Task / Project / Event expose `title`, Meeting / Document / Goal `name`.
    // A plain getter, so the glist can read it as `_.get(model, fieldId)`.
    get title(): string {
        return (this.relatedItem as any).title || (this.relatedItem as any).name || ''
    }

    // Set only on a project linked from an action-items board: the todo-view to
    // open, since the board has no url of its own.
    get projectViewId(): ID | null {
        return (this.metadata && this.metadata.projectViewId) || null
    }

    // The raw row only -- endpoint entities are deliberately not serialised
    // (they would walk back into the current item). For Collection's interface.
    toJSON(): any {
        return {
            id              : this.id,
            type            : this.type,
            metadata        : this.metadata,
            sourceItemType  : this.sourceItemType,
            sourceItemId    : this.sourceItemId,
            targetItemType  : this.targetItemType,
            targetItemId    : this.targetItemId,
        }
    }
}
